import { DateTime } from 'luxon';

/**
 * t241u: personal reminders ("Remind me" on a task). Times are Europe/London wall-clock times; the API stores an
 * instant. Quick picks: Later today 17:00 (only before 17:00), Tomorrow 09:00, Next week Mon 09:00, or Custom.
 */
export const REMINDER_TZ = 'Europe/London';
export type ReminderTargetType = 'task';
export interface MyReminder { id: string; target_type: ReminderTargetType; target_id: string; remind_at: string; sent_at: string | null }
export interface ReminderChoice { id: 'later' | 'tomorrow' | 'next_week'; label: string; at: string }

const london = (now: Date | DateTime) => (now instanceof Date ? DateTime.fromJSDate(now) : now).setZone(REMINDER_TZ).setLocale('en-GB');

export function reminderChoices(now: Date | DateTime = new Date()): ReminderChoice[] {
  const n = london(now);
  const out: ReminderChoice[] = [];
  const later = n.set({ hour: 17, minute: 0, second: 0, millisecond: 0 });
  if (later > n.plus({ minutes: 5 })) out.push({ id: 'later', label: 'Later today', at: later.toUTC().toISO()! });
  const tomorrow = n.plus({ days: 1 }).set({ hour: 9, minute: 0, second: 0, millisecond: 0 });
  out.push({ id: 'tomorrow', label: 'Tomorrow', at: tomorrow.toUTC().toISO()! });
  const monday = n.startOf('week').plus({ weeks: 1 }).set({ hour: 9, minute: 0, second: 0, millisecond: 0 });
  out.push({ id: 'next_week', label: 'Next week', at: monday.toUTC().toISO()! });
  return out;
}

/** "17:00", "Tue 09:00", "Mon 2 Nov 09:00": for the picker rows. */
export function reminderWhen(at: string, now: Date | DateTime = new Date()): string {
  const n = london(now), d = DateTime.fromISO(at).setZone(REMINDER_TZ).setLocale('en-GB');
  const days = Math.round(d.startOf('day').diff(n.startOf('day'), 'days').days);
  if (days === 0) return `Today ${d.toFormat('HH:mm')}`;
  if (days === 1) return `Tomorrow ${d.toFormat('HH:mm')}`;
  if (days > 1 && days < 7) return d.toFormat('ccc HH:mm');
  return d.toFormat(d.year === n.year ? 'ccc d LLL HH:mm' : 'd LLL yyyy HH:mm');
}

/** What the task shows: "Reminder Tue 09:00". */
export const reminderLabel = (r: Pick<MyReminder, 'remind_at'>, now: Date | DateTime = new Date()) => `Reminder ${reminderWhen(r.remind_at, now)}`;

/** A custom London date + time ("2026-10-27", "14:30") as an instant; null when invalid. */
export function reminderFromLocal(date: string, time: string): string | null {
  const d = DateTime.fromISO(`${date}T${time || '09:00'}`, { zone: REMINDER_TZ });
  return d.isValid ? d.toUTC().toISO()! : null;
}

/** Server check: in the future (1 minute's grace) and at most a year ahead. */
export function reminderTimeError(at: string, now: Date = new Date()): string | null {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return 'Pick a valid date and time';
  if (t < now.getTime() - 60_000) return 'Pick a time in the future';
  if (t > now.getTime() + 366 * 86_400_000) return 'Pick a time within a year';
  return null;
}
